import { HubTask } from './types';

const PAST = new Set(['approved', 'closed']);

export const isPastTask = (task: HubTask): boolean => PAST.has(task.status);

export interface TaskGroup {
  runId: string;
  tasks: HubTask[];
  current: boolean;
}

const dueStamp = (group: TaskGroup): string =>
  group.tasks
    .map((task) => task.due_at)
    .filter(Boolean)
    .sort()[0] ?? '';

const byDue = (left: TaskGroup, right: TaskGroup): number =>
  dueStamp(left).localeCompare(dueStamp(right));

/** One card per workflow run. A run stays current while any of its tasks is still open. */
export const groupTasks = (tasks: HubTask[]): { current: TaskGroup[]; past: TaskGroup[] } => {
  const byRun = new Map<string, HubTask[]>();
  tasks.forEach((task) => {
    const runId = task.process_run_id || task.id;
    const rows = byRun.get(runId) ?? [];
    rows.push(task);
    byRun.set(runId, rows);
  });

  const groups = Array.from(byRun.entries()).map(([runId, rows]) => ({
    runId,
    tasks: [...rows].sort((left, right) =>
      left.dimension_item_name.localeCompare(right.dimension_item_name),
    ),
    current: rows.some((task) => !isPastTask(task)),
  }));

  return {
    current: groups.filter((group) => group.current).sort(byDue),
    past: groups.filter((group) => !group.current).sort((left, right) => byDue(right, left)),
  };
};

export const primaryTaskId = (group: TaskGroup): string => {
  const open = group.tasks.find((task) => !isPastTask(task));
  return (open ?? group.tasks[0]).id;
};

/**
 * The shared plan name. Issued titles end with the item, as in
 * "Expenses planning · London" or "Expenses planning — London".
 */
export const planName = (title: string, itemName: string): string => {
  const name = itemName.trim();
  const heading = title.trim();
  if (!name || !heading.endsWith(name)) return heading || 'Planning task';
  const stripped = heading
    .slice(0, heading.length - name.length)
    .replace(/[\s:·•|–—-]+$/u, '')
    .trim();
  return stripped || heading;
};

/** Titles include the item name. The card uses the shared wording. */
export const runTitle = (tasks: HubTask[]): string => {
  const stripped = tasks.map((task) => {
    const name = task.dimension_item_name.trim();
    const title = task.title.trim();
    if (!name || !title.endsWith(name)) return title;
    return title
      .slice(0, title.length - name.length)
      .replace(/[\s:·•|–—-]+$/u, '')
      .trim();
  });
  const unique = Array.from(new Set(stripped.filter(Boolean)));
  if (unique.length === 1) return unique[0];
  return tasks[0]?.cycle_label || tasks[0]?.title || 'Planning task';
};
